import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { API_VERSION, APP_VERSION, PLATFORM_ID_ONLINE_LIFECYCLE } from '../config/app-version.const';
import { isStaticAsset } from './static-asset.util';

/** Verrou : un seul refresh réactif (401) en vol, les requêtes concurrentes patientent dessus. */
let refreshAndRetry: Promise<void> | null = null;

function withAuthHeaders(req: HttpRequest<unknown>, auth: AuthService): HttpRequest<unknown> {
  const token = auth.accessToken();
  const isFormData = req.body instanceof FormData;

  return req.clone({
    setHeaders: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: `application/vnd.iman.v${API_VERSION}+json, application/json, text/plain, */*`,
      'App-Version': APP_VERSION,
      'm-plateforme': PLATFORM_ID_ONLINE_LIFECYCLE,
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    },
  });
}

/**
 * Attache les headers d'API (Authorization, Accept versionné, App-Version, m-plateforme=7)
 * et gère le 401 réactif : un seul refresh en vol, puis rejeu de la requête d'origine.
 *
 * @remarks
 * Les appels vers /connect/token (login/refresh, domaine de Front) passent intouchés :
 * ils portent leur propre Content-Type form-urlencoded et ne doivent pas être rejoués.
 */
export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  // Un fichier statique (locale, image) ne porte pas de session : sortir avant d'injecter
  // AuthService, dont la construction déclencherait une requête qui repasserait ici (NG0200)
  if (isStaticAsset(req.url)) return next(req);

  const auth = inject(AuthService);
  const isTokenEndpoint = req.url.includes('/connect/token');
  const authReq = isTokenEndpoint ? req : withAuthHeaders(req, auth);

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || isTokenEndpoint) {
        return throwError(() => error);
      }

      refreshAndRetry ??= auth.refresh().finally(() => (refreshAndRetry = null));

      return from(refreshAndRetry).pipe(
        switchMap(() => next(withAuthHeaders(req, auth))),
        catchError((refreshError: unknown) => {
          auth.logout();
          return throwError(() => refreshError);
        }),
      );
    }),
  );
}
